import React from "react";
import { Button } from "../../../components";
import { getCfmFile } from "../../../redux/slices/CurrentUserSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { downloadFile } from "../../../utils/fileDownloads";
import { saveAs } from "file-saver";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const IntegrateConfigure = ({ isAdminPage, onBack }: { isAdminPage?: boolean; onBack?: () => void }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.currentUser.currentUser);
  const { currentOffice } = useSelector((state: RootState) => state.office);

  const handleDownloadFile = async () => {
    // const officeId = user?.offices?.[0]?.officeId || currentOffice?.officeId;
    const officeId = currentOffice?.officeId;

    if (officeId) {
      const res = await dispatch(getCfmFile({ officeId: officeId, toolbar: false }));
      if (res?.payload) {
        const url = res.payload as string;

        // Create temp link
        const link = document.createElement("a");
        link.href = url;
        link.download = ""; // empty => browser uses original file name from S3
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    }
  };

  const guideUrl = process.env.REACT_APP_GUIDE_KEY || "";
  const handleDownloadGuide = () => {
    const pdfUrl = guideUrl;
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };
  const pssUiTeam = "https://help.pssuiteemr.com/5.23/on/en/Content/04_Custom_forms/Importing_custom_forms.htm";
  return (
    <div>
      <div>
        {onBack && (
          <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onBack}>
            <ArrowBackIcon />
            Back
          </div>
        )}
      </div>
      <div className="flex justify-between items-center my-3">
        <div className="flex items-center gap-3">
          <h1 className="font-semibold mb-3">Setup & Integration Guide</h1>
        </div>
        <div className="w-fit">
          <Button text="Download .cfm" onClick={handleDownloadFile} />
        </div>
      </div>
      <div>
        <div className="max-w-4xl mx-auto p-6 space-y-8">
          {/* Intro */}
          <div>
            <p className="mt-2 text-gray-700">
              The Docnote PS Suite custom form allows your clinic to manage uninsured service requests directly within the PS Suite EMR. With it, you
              can:
            </p>
            <ul className="list-disc list-inside mt-3 text-gray-700 space-y-1">
              <li>Send sick notes and patient letters directly to patients</li>
              <li>Initiate prescription refill requests</li>
              <li>Submit any other uninsured service requests</li>
            </ul>
          </div>

          {/* Step 1 */}
          <div>
            <h3 className="text-lg font-semibold">Step 1 – Add Your Medical Practitioner Number (MPN) &amp; Signature</h3>
            <p className="mt-3 text-gray-700 italic">
              Note: This step must be completed individually by every healthcare professional who will be signing off on sick notes or patient
              letters. If your clinic has more than one provider, each provider will need to log in to their own Docnote account and complete the
              steps below.
            </p>
            <ol className="list-decimal list-inside mt-3 text-gray-700 space-y-2">
              <li>Click the profile icon in the top-right corner of Docnote.</li>
              <li>
                A side menu will open. Scroll down to locate the following fields:
                <ul className="list-disc list-inside ml-6 mt-1">
                  <li>
                    <strong>Medical Practitioner Number (MPN)</strong> — this will be your CPSO, CNO, or other applicable licensing number
                  </li>
                  <li>
                    <strong>Signature Upload</strong>
                  </li>
                </ul>
              </li>
              <li>Enter your MPN exactly as it appears in your PS Suite EMR.</li>
              <li>
                Upload or drag and drop your signature image into the box provided (<strong>PNG</strong> format required).
              </li>
              <li>Click Save at the bottom of the user profile screen.</li>
              <li>A success message will confirm your information has been updated.</li>
            </ol>
          </div>

          {/* Step 2 */}
          <div>
            <h3 className="text-lg font-semibold">Step 2 – Set Up Your Letterhead</h3>
            <p className="mt-3 text-gray-700 italic">
              Note: If your letterhead has already been configured during onboarding, you can skip this step.
            </p>
            <ol className="list-decimal list-inside mt-3 text-gray-700 space-y-2">
              <li>
                From the left-side menu, click <strong>Office Info</strong>.
              </li>
              <li>
                Click the <strong>Edit</strong> button in the top-right corner of the Office Info page.
              </li>
              <li>
                Scroll down to the <strong>Letterhead Configuration</strong> section and fill in your preferred details.
              </li>
              <li>
                Click <strong>Save</strong> at the top of the page to apply your changes.
              </li>
            </ol>
          </div>

          {/* Step 3 */}
          <div>
            <h3 className="text-lg font-semibold">Step 3 – Import the Custom Form into PS Suite EMR</h3>

            {/* Part A */}
            <h4 className="mt-3 font-semibold">Part A — Download the Form from Docnote</h4>
            <p className="mt-2 text-gray-700">
              If you are reading these instructions, you are already on the correct page — simply click the orange <strong>Download .cfm</strong>{" "}
              button at the top of the page.
            </p>
            <p className="mt-2 text-gray-700">Alternatively, you can navigate here manually:</p>
            <ol className="list-decimal list-inside ml-4 mt-2 text-gray-700 space-y-1">
              <li>
                Go to <strong>Office Info</strong>.
              </li>
              <li>
                Click the <strong>Integration</strong> tab.
              </li>
              <li>
                Select <strong>PSS Configuration</strong>.
              </li>
              <li>
                Click <strong>Download .cfm</strong>. A <code>.cfm</code> file will be saved to your computer.
              </li>
            </ol>

            {/* Part B */}
            <h4 className="mt-5 font-semibold">Part B — Import the Form into PS Suite EMR</h4>
            <ol className="list-decimal list-inside ml-4 mt-2 text-gray-700 space-y-1">
              <li>Open PS Suite EMR and log in.</li>
              <li>Select the Records window.</li>
              <li>
                Navigate to <strong>Settings &gt; Edit Custom Forms</strong>.
              </li>
              <li>
                You can either drag and drop the downloaded <code>.cfm</code> file directly into this window (then skip to step 8), or continue with
                steps 5–7 to import manually.
              </li>
              <li>
                In the forms menu, click <strong>File &gt; Import Form(s)</strong>.
              </li>
              <li>
                Select the <code>.cfm</code> file downloaded from Docnote: <code>DnForms-[office name][characters]</code>.
              </li>
              <li>
                Click <strong>Choose</strong>.
              </li>
              <li>A confirmation message will indicate the form was successfully imported.</li>
            </ol>

            <p className="mt-3 text-red-600 font-semibold">
              Important: Do not rename this form. The filename is used to establish the connection between the custom form and the Docnote Toolbar.
              Renaming it will break that connection.
            </p>
          </div>

          {/* Additional Resources */}
          <div>
            <h3 className="text-lg font-semibold">Additional Resources</h3>
            <ul className="list-disc list-inside mt-3 text-gray-700 space-y-2">
              <li>
                <a href={pssUiTeam} className="text-blue-600 hover:underline" target="_blank" rel="noreferrer">
                  Official TELUS PS Suite EMR Documentation — Importing Custom Forms
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="w-fit">
        <Button text="Download Guide" onClick={handleDownloadGuide} />
      </div>
    </div>
  );
};
export default IntegrateConfigure;
